import { Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import CardActions from "@material-ui/core/CardActions";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import { Warning } from "@material-ui/icons";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import Pagination from "@material-ui/lab/Pagination";
import { useQuery } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic";
import TableBackdrop from "components/Backdrops/TableBackdrop/TableBackdrop";
import CardTableContainer from "components/Cards/Tables/CardTableContainer";
import { listCooperativeMembers } from "crud/cooperativeMembers.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import moment from "moment";
import { useFormContext } from "react-hook-form";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const tableHead = [
  { label: " ", sortKey: null },
  { label: "Data de Registo", sortKey: "created" },
  { label: "Nome", sortKey: "name" },
  { label: "NIF", sortKey: "nif" },
  { label: "N.º Documento", sortKey: "identityDocumentNumber" },
  { label: "Função", sortKey: "functionDescription" },
  { label: "Estado", sortKey: "isActive" },
  { label: "Data de Actualização", sortKey: "lastModified" },
];

const ResultSection = ({ queryParams, sortColumn, sortDirection, onSortChange, onPageChange, onViewDetails }) => {
  const theme = useTheme();
  const classes = useStylesGeneric();

  const { watch, setValue } = useFormContext();
  const page = watch("pageIndex") ?? 1;

  const { data, isLoading } = useQuery({
    queryKey: ["listCooperativeMembers", queryParams],
    queryFn: () => listCooperativeMembers(queryParams),
    enabled: !!queryParams,
    keepPreviousData: true,
    select: (response) => {
      if (!response) return { data: [], totalCount: 0, totalPages: 0 };
      return {
        data: response.data ?? [],
        totalCount: response.totalCount ?? 0,
        totalPages: response.totalPages ?? 0,
      };
    },
  });

  const handleSort = (columnIndex) => {
    const column = tableHead[columnIndex].sortKey;
    if (!column) return;

    const newDirection = sortColumn === column ? (sortDirection === "asc" ? "desc" : "asc") : "asc";

    setValue("pageIndex", 1);
    onSortChange?.(column, newDirection);
  };

  const renderSortIcon = (columnIndex) => {
    const column = tableHead[columnIndex].sortKey;
    if (!column) return null;

    if (sortColumn === column) {
      return sortDirection === "desc" ? (
        <ArrowUpwardIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle" }} />
      ) : (
        <ArrowDownwardIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle" }} />
      );
    }
    return <UnfoldMoreIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle", opacity: 0.3 }} />;
  };

  const handlePageChange = (_, value) => {
    setValue("pageIndex", value);
    onPageChange?.(value);
  };

  return (
    <CardTableContainer title="Lista de Membros da Cooperativa" count={data?.totalCount}>
      <TableContainer>
        <ConditionGuard condition={!isLoading} fallback={<TableBackdrop open />}>
          <Box component={Table} alignItems="center" marginBottom="0!important">
            <TableHead>
              <TableRow>
                {tableHead.map((col, key) => (
                  <TableCell
                    key={key}
                    classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}
                    onClick={() => col.sortKey && handleSort(key)}
                    style={col.sortKey ? { cursor: "pointer", userSelect: "none" } : {}}
                  >
                    <Box display="flex" alignItems="center">
                      {col.label}
                      {col.sortKey && renderSortIcon(key)}
                    </Box>
                  </TableCell>
                ))}
                <TableCell
                  classes={{
                    root:
                      classes.tableCellRoot + " " + classes.tableCellRootHead + " " + classes.tableCellHeadStickyRight,
                  }}
                >
                  Detalhes
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.data?.length > 0 ? (
                data.data.map((member, key) => (
                  <TableRow key={key}>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {member.isExpiryWithin30Days && (
                        <Tooltip title="Documento de identificação a expirar em breve ou expirado." placement="top">
                          <Warning color="error" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {member.created ? moment(member.created).format("DD-MM-YYYY") : "-"}
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{member.name}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{member.nif}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {member.identityDocumentNumber ?? "-"}
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>{member.functionDescription ?? "-"}</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {member.isActive ? "Activo" : "Inactivo"}
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot }}>
                      {member.lastModified ? moment(member.lastModified).format("DD-MM-YYYY") : "-"}
                    </TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellBodyStickyRight }}>
                      <Tooltip title="Detalhes">
                        <IconButton onClick={() => onViewDetails?.(member.id)}>
                          <Box
                            component={ArrowForwardIosIcon}
                            width="12px!important"
                            height="12px!important"
                            color={theme.palette.primary.main}
                          />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" classes={{ root: classes.tableCellRoot }}>
                    <Typography variant="body2">Sem membros registados.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Box>
        </ConditionGuard>
      </TableContainer>

      <Box classes={{ root: classes.cardActionsRoot }} component={CardActions} justifyContent="flex-end" />

      <ConditionGuard condition={(data?.totalPages ?? 0) > 1}>
        <Pagination
          className={classes.alignPagination}
          count={data?.totalPages ?? 0}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </ConditionGuard>
    </CardTableContainer>
  );
};

export default ResultSection;
